/* eslint-disable no-console */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { atom, useSetAtom } from 'jotai';

import { useAuth } from '@restworld/feature-authentication';
import { trackEmployerFreekiumSignupGtag, useAnalytics, useFacebookPixel, useKv } from '@restworld/utils-common';
import {
  CreateEmployerRestaurantPayload,
  useOrganizationsService,
  useRestaurantsService,
  ICreateOrganizationPayload,
} from '@restworld/data-services';

import { activeRestaurantOnboardingStateAtom } from '.';
import { useFetchEmployerRestaurantsQueryKey } from '../hooks';
import { newlyCreatedRestaurantIdAtom, newlyCreatedRestaurantSlugAtom } from './restaurant-onboarding-successful';

export const newlyCreatedOrganizationIdAtom = atom('');

export const useUploadRestaurantPicturesMutation = () => {
  const createRestaurantService = useRestaurantsService();
  return useMutation({
    mutationFn: (params: { restaurantId: string; pictures: File[] }) =>
      createRestaurantService.uploadPicture({
        restaurantId: params.restaurantId,
        file: params.pictures,
      }),
    mutationKey: ['upload-restaurant-pictures'],
    onError(error) {
      console.error('Error uploading restaurant pictures', error);
    },
  });
};

const useRestaurantOnboardingMutationQuery = () => {
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const setNewlyCreatedRestaurantId = useSetAtom(newlyCreatedRestaurantIdAtom);
  const setNewlyCreatedRestaurantSlug = useSetAtom(newlyCreatedRestaurantSlugAtom);
  const createRestaurantService = useRestaurantsService();
  const { mutate: uploadPictures } = useUploadRestaurantPicturesMutation();

  const queryClient = useQueryClient();
  const refetchRestaurantsQueryKy = useFetchEmployerRestaurantsQueryKey();
  const refetchEmployerRestaurants = () => queryClient.invalidateQueries({ queryKey: refetchRestaurantsQueryKy });
  const { user } = useAuth();
  const { setKV } = useKv();
  const kvKey = `${user?.id}-newly-created-venue-id`;
  const { trackPHEvent } = useAnalytics();

  return useMutation({
    mutationFn: (params: CreateEmployerRestaurantPayload) => {
      const { binary_pictures, ...rest } = params;
      return createRestaurantService.createRestaurant({ ...rest, organization_id: user?.organization_id });
    },
    mutationKey: ['create-restaurant'],
    onSuccess(data, variables) {
      const restaurantId = data?.data?.id || '';
      restaurantId && setKV({ key: kvKey, value: restaurantId, type: 'employer_newly_created_venue_id' });
      if (variables.binary_pictures && variables.binary_pictures.length > 0)
        uploadPictures({ restaurantId, pictures: variables.binary_pictures });
      setActiveUI('restaurant_onboarding_successfull');
      refetchEmployerRestaurants();
      setNewlyCreatedRestaurantId(restaurantId);
      setNewlyCreatedRestaurantSlug(data?.data?.slug || '');

      trackPHEvent('freemium_employer_confirms_restaurant_data', {});
    },
    onError(error) {
      console.error('Error creating restaurant', error);
    },
  });
};
export default useRestaurantOnboardingMutationQuery;

export const useOrganizationRestaurantOnboardingMutationQuery = () => {
  const orgServices = useOrganizationsService();
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const setNewlyCreatedOrganizationId = useSetAtom(newlyCreatedOrganizationIdAtom);
  const setNewlyCreatedRestaurantId = useSetAtom(newlyCreatedRestaurantIdAtom);
  const setNewlyCreatedRestaurantSlug = useSetAtom(newlyCreatedRestaurantSlugAtom);
  const { initializeAuthentication } = useAuth();
  const { trackEmployerSignup } = useFacebookPixel();
  const { trackPHEvent } = useAnalytics();
  const { mutate: uploadPictures } = useUploadRestaurantPicturesMutation();

  return useMutation({
    mutationFn: (params: ICreateOrganizationPayload) => {
      const {
        employer_user_contacts,
        restaurants: { binary_pictures, ...rest },
      } = params;
      return orgServices.createOrganization({ employer_user_contacts, restaurants: { ...rest } });
    },
    mutationKey: ['create-organization-restaurant'],
    onSuccess(data, variables) {
      trackEmployerSignup();
      trackPHEvent('freemium_employer_signup', {});
      trackEmployerFreekiumSignupGtag();
      setNewlyCreatedRestaurantId(data?.data?.['restaurant_id'] || '');
      setNewlyCreatedRestaurantSlug(data?.data?.['slug'] || '');
      setNewlyCreatedOrganizationId(data?.data?.organization_id || '');
      if (data?.data?.['jwt']) initializeAuthentication(data.data['jwt']);
      setActiveUI('restaurant_onboarding_successfull');
      if (variables.restaurants.binary_pictures && variables.restaurants.binary_pictures.length > 0)
        uploadPictures({
          restaurantId: data?.data?.['restaurant_id'],
          pictures: variables.restaurants.binary_pictures,
        });
    },
    onError(error) {
      console.error('Error creating organization restaurant', error);
    },
  });
};
